import React, { useState } from "react";
import { useParams } from "react-router";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQuery } from "@apollo/client/react";
import { FAVORITE_REPORTS_QUERY } from "../../../constants/graphql/index";
import ReportsTable from "./ReportsTable";
import ReportsMobileView from "./ReportsMobileView";
import useAuth from "../../../hooks/useAuth";
import AssignTemplateModal from "./AssignTemplateModal";
import useRoles from "../../../hooks/user/useRoles";

const styles = {
  tab: {
    textTransform: "none",
  },
  addButton: {
    padding: "6px",
    minWidth: 0,
  },
  searchField: {
    minWidth: 250,
  },
};

export default function Reports() {
  const { projectId } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const hasRestrictedAccess = useRoles(["client", "employee", "shopper", "viewer"]);
  const { data: { favoriteReports = [] } = {}, loading } = useQuery(FAVORITE_REPORTS_QUERY, {
    variables: { projectId, userReportsOnly: hasRestrictedAccess },
  });
  const { settings } = user.company;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearchChange = event => {
    setSearch(event.target.value);
  };

  const handleAddTemplate = () => {
    setAssignModalOpen(true);
  };

  return (
    <Paper>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        pb={0}
        flexDirection={{ xs: "column-reverse", sm: "row" }}
        gap={1}
      >
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="reports tabs">
          <Tab label="Favorite Reports" sx={styles.tab} />
        </Tabs>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          width={{ xs: "100%", sm: "auto" }}
          justifyContent="flex-end"
        >
          {!hasRestrictedAccess && (
            <Tooltip title={`Assign Template to ${settings.filter_field2}`} arrow>
              <Button
                variant="contained"
                color="primary"
                sx={styles.addButton}
                onClick={handleAddTemplate}
              >
                <AddCircleOutlineOutlinedIcon />
              </Button>
            </Tooltip>
          )}
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {search ? (
                    <IconButton onClick={() => setSearch("")} size="small" edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            }}
            sx={styles.searchField}
          />
        </Box>
      </Box>
      {loading ? (
        <Box py={3} display="flex" justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box p={2}>
          {isMobile ? (
            <ReportsMobileView favoriteReports={favoriteReports} search={search} />
          ) : (
            <ReportsTable displayAll={false} favoriteReports={favoriteReports} search={search} />
          )}
        </Box>
      )}
      {assignModalOpen && (
        <AssignTemplateModal open onClose={() => setAssignModalOpen(false)} projectId={projectId} />
      )}
    </Paper>
  );
}
